import { useContext } from "react";
import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import { useFormik } from "formik";
import * as Yup from "yup";

import GoogleIcon from "../assets/images/google"
import FacebookIcon from "../assets/images/facebook";

import { TextField, Box, Button, Link, Divider, Alert, FormGroup, 
        FormControlLabel, Checkbox, Typography, Backdrop } from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';

import useApi from "../services/api";
import { GlobalContext } from "../contexts/global-context";
import { useConfig } from "../contexts/config-context";
import { AuthContext } from "../contexts/auth-context";
import { StandardLoading } from "./standard-loading";
import { useSocialAuth } from "../hooks/use-social-auth";

RegisterForm.propTypes = {
    onShowRegToast: PropTypes.func,
};

export function RegisterForm(props) {
    const { registerUser } = useApi();
    const { setEmail, setSessionToken } = useContext(AuthContext);
    const { setAuthModalType } = useContext(GlobalContext);
    const { config } = useConfig();
    const { handleGoogleAuth, handleFacebookAuth, oauthLoading, generalError, setGeneralError } = useSocialAuth();

    const formik = useFormik({
        initialValues: {
            username: "",
            email: "",
            password1: "",
            password2:"",
            promo_consent: true,
        },
        validationSchema: Yup.object({
            username: Yup.string()
                .required("Required"),
            email: Yup.string()
                .min(2, "Names must have at least 2 characters")
                .max(100, "Names can't be longer than 100 characters")
                .email("Must be a valid email address")
                .required("Required"),
            password1: Yup.string()
                .matches(
                    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                    "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character [@$!%*#?&]")
                .required("Required"),
            password2: Yup.string()
                .oneOf([Yup.ref('password1'), null], 'Passwords must match')
                .required("Required"),
            promo_consent: Yup.bool()
                .required()
                .oneOf([true, false])
        }),
        onSubmit: values => {
            // clear any previously rendered errors
            setGeneralError({
                isErrorHidden: true,
                errorMessage: ""
            });

            // Prepare payload for new signup endpoint - single password field
            const signupData = {
                username: values.username,
                email: values.email,
                password: values.password1,  // Use password1 as the single password
                promo_consent: values.promo_consent
            };

            registerUser(signupData)
                .then((response) => {
                    if (response.status === 201) {
                        ReactGA.event({ category: 'auth', action: 'sign_up' });
                        setEmail(values.email);
                        setSessionToken(response.data?.meta?.session_token);
                        setAuthModalType("emailed");
                    }
                })
                .catch((err) => {
                    if (err?.response?.data?.data?.flows) {
                    }
                    
                    const res = err?.response;
                    
                    // Handle new allauth flow - 401 with verify_email pending
                    if (res?.status === 401 && res?.data?.data?.flows) {
                        const verifyEmailFlow = res.data.data.flows.find(flow => flow.id === 'verify_email');
                        if (verifyEmailFlow?.is_pending) {
                            // Email verification required - show code entry modal
                            setEmail(values.email);
                            setSessionToken(res.data.meta?.session_token);
                            setAuthModalType("emailed");
                            return;
                        }
                    }
                    
                    if (res?.status === 400 && res?.data) {
                        // Check for new allauth error format
                        if (res.data.errors && Array.isArray(res.data.errors)) {
                            // Handle new format with errors array
                            let fieldErrors = {};
                            let generalErrors = [];
                            
                            res.data.errors.forEach(error => {
                                if (error.param && Object.prototype.hasOwnProperty.call(formik.values, error.param)) {
                                    // Field-specific error
                                    fieldErrors[error.param] = error.message;
                                } else {
                                    // General error
                                    generalErrors.push(error.message);
                                }
                            });
                            
                            if (Object.keys(fieldErrors).length > 0) {
                                formik.setErrors(fieldErrors);
                            }
                            if (generalErrors.length > 0) {
                                setGeneralError({
                                    isErrorHidden: false,
                                    errorMessage: generalErrors.join(" ")
                                });
                            }
                        } else {
                            // Handle old format
                            let obj = {};
                            for (const k in res.data) {
                                if (Object.prototype.hasOwnProperty.call(formik.values, k)) {
                                    obj[k] = res.data[k][0];  // Only use first error string
                                } else {
                                    setGeneralError({
                                        isErrorHidden: false,
                                        errorMessage: res.data[k].join(" ")
                                    });
                                }
                            }
                            formik.setErrors(obj);
                        }
                    } else {
                        // Generic network or unexpected error
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: err.message || "Unexpected error"
                        });
                    }
                })
                .finally(() => {
                    formik.setSubmitting(false);
                });

        }
    });
    return (
        <>
                   <form onSubmit={formik.handleSubmit}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    mt: 0,
                    m: 'auto'
                }}>
                    { !generalError.isErrorHidden ? (
                        <Alert sx={{mb: 1}} severity="error"> {generalError.errorMessage} </Alert>
                    ):null}
                    <TextField 
                        id="username" 
                        label="Username" 
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.username}
                        disabled={formik.isSubmitting}
                        error={formik.touched.username && Boolean(formik.errors.username)}
                        helperText={formik.touched.username && formik.errors.username}
                        />
                    <TextField 
                        id="email" 
                        label="Email Address" 
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        disabled={formik.isSubmitting}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        />
                    <TextField 
                        type="password"
                        id="password1" 
                        label="Password" 
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password1}
                        disabled={formik.isSubmitting}
                        error={formik.touched.password1 && Boolean(formik.errors.password1)}
                        helperText={formik.touched.password1 && formik.errors.password1}
                        />
                    <TextField 
                        type="password"
                        id="password2" 
                        label="Confirm Password" 
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password2}
                        disabled={formik.isSubmitting}
                        error={formik.touched.password2 && Boolean(formik.errors.password2)}
                        helperText={formik.touched.password2 && formik.errors.password2}
                        />
                        <FormGroup>
                            <FormControlLabel control={
                                    <Checkbox size="small"
                                        id="promo_consent"
                                        onChange={formik.handleChange}
                                        checked={formik.values.promo_consent}
                                        disabled={formik.isSubmitting}/>}
                                        label={
                                        <Typography variant="body2" component="h3">
                                            Get updates & news from Plancrafted
                                        </Typography>}/>
                        </FormGroup>
                    <LoadingButton
                        sx={{mt: 2}} 
                        variant="contained"
                        fullWidth
                        loading={formik.isSubmitting}
                        type="submit"
                        >
                            <span>Sign up</span>
                    </LoadingButton>
                    
                    {/* Social Login Section - Only show if at least one SSO is enabled */}
                    {(config?.features?.sso?.google_enabled || config?.features?.sso?.facebook_enabled) && (
                        <>
                            <Divider sx={{m: 2}}>or</Divider>
                            
                            {config?.features?.sso?.google_enabled && (
                                <Button
                                    sx={{m: 1, color: 'text.primary' }}
                                    startIcon={<GoogleIcon />}
                                    variant="outlined"
                                    onClick={handleGoogleAuth}>
                                        Continue with Google
                                </Button>
                            )}
                            
                            {config?.features?.sso?.facebook_enabled && (
                                <Button
                                    sx={{ m: 1, color: 'text.primary' }} 
                                    startIcon={<FacebookIcon />}
                                    variant="outlined"
                                    onClick={handleFacebookAuth}>
                                        Continue with Facebook
                                </Button>
                            )}
                        </>
                    )}
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <span> Have an account?&nbsp;</span>
                        <Link
                            onClick={() => setAuthModalType("login")} 
                            component="button">
                            Log in
                        </Link>
                    </Box>
                </Box>
            </form>
            
            {/* OAuth Loading Backdrop - matching site patterns */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute'
                }}
                open={oauthLoading}
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 2
                }}>
                    <StandardLoading size={60} color='inherit' />
                    <Typography variant="h6" color="inherit">
                        Completing authentication...
                    </Typography>
                    <Typography variant="body2" color="inherit">
                        Please complete sign-in in the popup window
                    </Typography>
                </Box>
            </Backdrop>
        </>
    );
}